import React, {
    useState,
    useEffect,
    useMemo,
    useImperativeHandle,
    forwardRef,
} from 'react'
import TabedLists from './common/TabedLists'
import { Button, Box, useTheme, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Pagination } from 'packages/eid-ui'
import { Icon } from 'packages/eid-icons'
import Search from './common/Search'
import { useModalSearchForAssignmentPopup } from 'hooks/assignmentsPopupHooks'

const PaginationMain = styled(Box)({
    width: '26rem !important',
    marginTop: '1.5rem',
    '& div:first-of-type': { justifyContent: 'center' },
    '& .MuiBox-root': { padding: '0 !important' },
    '& ul': {
        height: '3rem',
        borderRadius: '2rem',
        flexWrap: 'nowrap !important',
        '& li button': { padding: '0 0.2rem' },
    },
})
const SearchBar = styled(Box)({
    width: '26rem !important',
    height: '3.2rem',
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.1)',
    '& div': {
        borderRadius: '4px',
    },
})

const RemoveButton = styled(Button)(({ theme, disabled }) => ({
    height: '3.1rem',
    width: '21.8rem',
    border: !disabled
        ? `solid 0.1rem ${theme?.palette.error.main} !important`
        : `solid 0.1rem ${theme?.palette.grey['900']}`,
    color: !disabled
        ? `${theme?.palette.error.main} !important`
        : `${theme?.palette.grey['900']}`,

    '& span': {
        alignItems: 'center',
    },
    '& svg': {
        width: '1.2rem',
        position: 'absolute',
        left: '0.5rem',
        color: !disabled
            ? `${theme?.palette.error.main} !important`
            : `${theme?.palette.grey['900']}`,
        fill: !disabled
            ? `${theme?.palette.error.main} !important`
            : `${theme?.palette.grey['900']}`,
    },
}))

const AddButton = styled(Button)(({ theme, disabled }) => ({
    height: '3.1rem',
    border: !disabled
        ? `solid 0.1rem ${theme?.palette.common.white} !important`
        : `solid 0.1rem ${theme?.palette.grey['900']}`, // 1px = 0.1rem
    color: !disabled
        ? `${theme?.palette.common.white} !important`
        : `${theme?.palette.grey['900']}`,
    backgroundColor: !disabled ? theme?.palette.secondary.main : 'initial',
    '& span': {
        alignItems: 'center',
    },
    '& svg': {
        width: '1.2rem',
        position: 'absolute',
        alignItems: 'center',
        right: '0.5rem',
        color: !disabled
            ? `${theme?.palette.common.white} !important`
            : `${theme?.palette.grey['900']}`,
        fill: !disabled
            ? `${theme?.palette.common.white} !important`
            : `${theme?.palette.grey['900']}`,
    },
    '&:hover': {
        backgroundColor: !disabled ? '#01ae8fcc !important' : 'initial',
    },
}))

const ApplicationsSelection = forwardRef(
    (
        {
            assignedUrl,
            unAssignedUrl,
            assignedQueryFilters,
            unAssignedQueryFilters,
        },
        ref,
    ) => {
        const theme = useTheme()
        const { t } = useTranslation()

        const getTabItems = (tabLabel) => {
            const leftTab = tabsLeft.find((t) => t.label === tabLabel)
            const rightTab = tabsRight.find((t) => t.label === tabLabel)
            if (leftTab) {
                return leftTab?.items
            }
            if (rightTab) {
                return rightTab?.items
            }
        }
        const getFormData = () => {
            return {
                Added: getTabItems('Added'),
                Removed: getTabItems('Removed'),
            }
        }

        useImperativeHandle(ref, () => ({
            getFormData,
        }))

        const [selectedItems, setSelectedItems] = useState({})

        const [disabledItems, setDisabledItems] = useState({
            All: new Set(),
            Assigned: new Set(),
        })
        const [searchTerm, setSearchTerm] = useState('')

        const [tabsLeft, setTabsLeft] = useState([
            {
                label: t('All'),
                items: [],
                page: 1,
                total: 0,
                countColor: theme?.palette.primary.main,
                isServerPaginated: true,
            },
            {
                label: t('Removed'),
                items: [],
                page: 1,
                total: 0,
                countColor: '#d0021b',
                isServerPaginated: false,
                showCrossIcon: true,
            },
        ])

        const [tabsRight, setTabsRight] = useState([
            {
                label: t('Assigned'),
                items: [],
                page: 1,
                total: 0,
                countColor: theme?.palette.primary.main,
                isServerPaginated: true,
            },
            {
                label: t('Added'),
                items: [],
                page: 1,
                total: 0,
                countColor: theme?.palette.success.main,
                isServerPaginated: false,
                showPlusIcon: true,
            },
        ])

        const [selectedTabs, setSelectedTabs] = useState({
            left: tabsLeft[0]?.label,
            right: tabsRight[0]?.label,
        })
        const [selectedPages, setSelectedPages] = useState({
            left: 1,
            right: 1,
        })

        const ITEMS_PER_PAGE = 5
        const skipLeft = useMemo(
            () => (selectedPages['left'] - 1) * ITEMS_PER_PAGE,
            [selectedPages['left']],
        )

        const skipRight = useMemo(
            () => (selectedPages['right'] - 1) * ITEMS_PER_PAGE,
            [selectedPages['right']],
        )

        const { data: leftData, isFetching: isFetchingLeft } =
            useModalSearchForAssignmentPopup(
                unAssignedUrl,
                {
                    ...unAssignedQueryFilters,
                    skip: skipLeft,
                    take: ITEMS_PER_PAGE,
                    ...(searchTerm ? { searchTerm } : {}),
                },
                Boolean(selectedTabs['left'] === 'All'),
            )

        const { data: rightData, isFetching: isFetchingRight } =
            useModalSearchForAssignmentPopup(
                assignedUrl,
                {
                    ...assignedQueryFilters,
                    skip: skipRight,
                    take: ITEMS_PER_PAGE,
                },
                Boolean(selectedTabs['right'] === 'Assigned'),
            )

        useEffect(() => {
            setSelectedPages({ ...selectedPages, left: 1 })
        }, [selectedTabs['left']])

        useEffect(() => {
            setSelectedPages({ ...selectedPages, right: 1 })
        }, [selectedTabs['right']])

        const handleTabUpdate = (tabType, data, selectedTab, isFetching) => {
            const current = tabType.find((t) => t.label === selectedTab)
            if (current.isServerPaginated && !isFetching && data) {
                return tabType.map((tab) =>
                    tab.label === selectedTab
                        ? {
                              ...current,
                              items: data.data,
                              total: data.totalCount,
                              page: selectedPages[
                                  tabType === tabsLeft ? 'left' : 'right'
                              ],
                          }
                        : tab,
                )
            }
            return tabType.map((tab) =>
                tab.label === selectedTab
                    ? {
                          ...current,
                          total: tab?.items?.length,
                          page: selectedPages[
                              tabType === tabsLeft ? 'left' : 'right'
                          ],
                      }
                    : tab,
            )
        }

        useEffect(() => {
            setTabsLeft((prev) =>
                handleTabUpdate(
                    prev,
                    leftData,
                    selectedTabs['left'],
                    isFetchingLeft,
                ),
            )
        }, [
            leftData,
            isFetchingLeft,
            selectedTabs['left'],
            skipLeft,
            searchTerm,
        ])

        useEffect(() => {
            setTabsRight((prev) =>
                handleTabUpdate(
                    prev,
                    rightData,
                    selectedTabs['right'],
                    isFetchingRight,
                ),
            )
        }, [
            rightData,
            isFetchingRight,
            selectedTabs['right'],
            skipRight,
            searchTerm,
        ])

        const handleSelectItem = (item, selectedTab) => {
            let existing = new Set(selectedItems[selectedTab] || [])
            const isSelected = [...existing].some(
                (existingItem) => existingItem.id === item.id,
            )

            if (isSelected) {
                existing = new Set(
                    [...existing].filter(
                        (existingItem) => existingItem.id !== item.id,
                    ),
                )
            } else {
                existing.add(item)
            }

            setSelectedItems({
                ...selectedItems,
                [selectedTab]: existing,
            })
        }

        const TAB_MAPPINGS = {
            All: { target: 'Added', disable: 'All' },
            Removed: { enable: 'Assigned', removeFromSource: true },
            Assigned: { target: 'Removed', disable: 'Assigned' },
            Added: { enable: 'All', removeFromSource: true },
        }

        const handleAddItems = (addAll = false, doubleClickedItem = null) => {
            const sourceLabel = selectedTabs['left']
            const { target, disable, enable, removeFromSource } =
                TAB_MAPPINGS[sourceLabel] || {}

            const sourceTab = tabsLeft.find((t) => t.label === sourceLabel)
            const targetTab = tabsRight.find((t) => t.label === target)

            if (!sourceTab || sourceTab.items.length === 0) return

            let itemsToAdd = doubleClickedItem
                ? new Set([doubleClickedItem])
                : addAll
                ? new Set(sourceTab.items)
                : selectedItems[sourceLabel]

            if (!itemsToAdd || itemsToAdd.size === 0) return

            if (targetTab) {
                const existingItems = new Set(
                    targetTab.items.map((item) => item.id),
                )
                itemsToAdd = new Set(
                    [...itemsToAdd].filter((id) => !existingItems.has(id)),
                )
            }
            if (itemsToAdd.size === 0) return

            if (disable) {
                setDisabledItems((prev) => ({
                    ...prev,
                    [disable]: new Set([
                        ...prev[disable],
                        ...[...itemsToAdd].map((item) => item.id),
                    ]),
                }))
            }
            if (enable) {
                setDisabledItems((prev) => ({
                    ...prev,
                    [enable]: new Set(
                        [...prev[enable]].filter(
                            (id) =>
                                ![...itemsToAdd]
                                    .map((item) => item.id)
                                    .includes(id),
                        ),
                    ),
                }))
            }

            if (target) {
                setTabsRight((prev) =>
                    prev.map((tab) =>
                        tab.label === target
                            ? {
                                  ...tab,
                                  items: [...tab.items, ...[...itemsToAdd]],
                                  total: tab.total + itemsToAdd?.size,
                              }
                            : tab,
                    ),
                )
            }

            if (removeFromSource) {
                setTabsLeft((prev) =>
                    prev.map((tab) =>
                        tab.label === sourceLabel
                            ? {
                                  ...tab,
                                  items: tab.items.filter(
                                      (item) =>
                                          ![...itemsToAdd].some(
                                              (addItem) =>
                                                  addItem.id === item.id,
                                          ),
                                  ),
                                  total: tab.total - itemsToAdd.size,
                              }
                            : tab,
                    ),
                )
            }
            if (!doubleClickedItem) {
                setSelectedItems((prev) => ({
                    ...prev,
                    [sourceLabel]: new Set(),
                }))
            } else {
                setSelectedItems((prev) => ({
                    ...prev,
                    [sourceLabel]: new Set(
                        [...(prev[sourceLabel] || [])].filter(
                            (id) => id !== doubleClickedItem.id,
                        ),
                    ),
                }))
            }
            setSelectedTabs({ left: sourceLabel, right: target || enable })
        }

        const handleRemoveItems = (
            removeAll = false,
            doubleClickedItem = null,
        ) => {
            const sourceLabel = selectedTabs['right']
            const { target, disable, enable, removeFromSource } =
                TAB_MAPPINGS[sourceLabel] || {}

            const sourceTab = tabsRight.find((t) => t.label === sourceLabel)

            if (!sourceTab || sourceTab.items.length === 0) return

            let itemsToRemove = doubleClickedItem
                ? new Set([doubleClickedItem])
                : removeAll
                ? new Set(
                      sourceTab.items.filter(
                          (item) =>
                              !tabsLeft
                                  .find((t) => t.label === target)
                                  ?.items.some(
                                      (targetItem) => targetItem.id === item.id,
                                  ),
                      ),
                  )
                : selectedItems[sourceLabel]

            if (!itemsToRemove || itemsToRemove.size === 0) return

            if (target) {
                setTabsLeft((prev) =>
                    prev.map((tab) =>
                        tab.label === target
                            ? {
                                  ...tab,
                                  items: [...tab.items, ...[...itemsToRemove]],
                                  total: tab.total + itemsToRemove.size,
                              }
                            : tab,
                    ),
                )
            }

            if (removeFromSource) {
                setTabsRight((prev) =>
                    prev.map((tab) =>
                        tab.label === sourceLabel
                            ? {
                                  ...tab,
                                  items: tab.items.filter(
                                      (item) =>
                                          ![...itemsToRemove].some(
                                              (removeItem) =>
                                                  removeItem.id === item.id,
                                          ),
                                  ),
                                  total: tab.total - itemsToRemove.size,
                              }
                            : tab,
                    ),
                )
            }

            if (disable) {
                setDisabledItems((prev) => ({
                    ...prev,
                    [disable]: new Set([
                        ...prev[disable],
                        ...[...itemsToRemove].map((item) => item.id),
                    ]),
                }))
            }

            if (enable) {
                setDisabledItems((prev) => ({
                    ...prev,
                    [enable]: new Set(
                        [...prev[enable]].filter(
                            (id) =>
                                ![...itemsToRemove]
                                    .map((item) => item.id)
                                    .includes(id),
                        ),
                    ),
                }))
            }

            setSelectedItems((prev) => ({
                ...prev,
                [sourceLabel]: new Set(),
            }))

            setSelectedTabs({ left: target || enable, right: sourceLabel })
        }

        return (
            <Box>
                <SearchBar marginBottom={'1rem'}>
                    <Search
                        placeholder={t('Search')}
                        handleSearch={(val) => {
                            setSearchTerm(val)
                        }}
                        handleClear={() => {
                            setSearchTerm('')
                        }}
                        showClearIcon={true}
                    />
                </SearchBar>

                <Box display="flex" flex={1}>
                    <Box>
                        <TabedLists
                            tabs={tabsLeft}
                            selectedTabValue={selectedTabs['left']}
                            onSelectedTabChange={(val) => {
                                setSelectedTabs({ ...selectedTabs, left: val })
                            }}
                            handleSelectItem={(item) =>
                                handleSelectItem(item, selectedTabs['left'])
                            }
                            checkIfSelected={(item) =>
                                [
                                    ...(selectedItems[selectedTabs['left']] ||
                                        []),
                                ].some(
                                    (selectedItem) =>
                                        selectedItem.id === item.id,
                                )
                            }
                            checkIfDisabled={(item) =>
                                disabledItems[selectedTabs['left']]?.has(
                                    item.id,
                                )
                            }
                            handleDoubleClick={(e, item) => {
                                handleAddItems(false, item)
                            }}
                            searchTerm={searchTerm}
                            isLoading={isFetchingLeft}
                        />

                        {tabsLeft.find((t) => t.label === selectedTabs['left'])
                            ?.total > 0 && (
                            <PaginationMain margin="5px">
                                {!isFetchingLeft && (
                                    <Pagination
                                        count={Math.ceil(
                                            tabsLeft.find(
                                                (t) =>
                                                    t.label ===
                                                    selectedTabs['left'],
                                            )?.total / ITEMS_PER_PAGE,
                                        )}
                                        size="large"
                                        page={selectedPages['left']}
                                        onChange={(_, value) =>
                                            setSelectedPages((prev) => ({
                                                ...prev,
                                                left: value,
                                            }))
                                        }
                                        variant="rounded"
                                    />
                                )}
                            </PaginationMain>
                        )}
                    </Box>

                    <Box
                        margin="10px"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                    >
                        <AddButton
                            onClick={() => {
                                handleAddItems()
                            }}
                            disabled={
                                !selectedItems[selectedTabs['left']] ||
                                selectedItems[selectedTabs['left']].size === 0
                            }
                            sx={{ marginBottom: '2rem' }}
                            endIcon={
                                <Icon
                                    name="ArrowSmall"
                                    color="#8b909a"
                                    fill="#8b909a"
                                    direction="right"
                                />
                            }
                            data-ai-explain="Add selected applications to category"
                        >
                            {t('Add')}
                        </AddButton>
                        <RemoveButton
                            onClick={() => {
                                handleRemoveItems()
                            }}
                            disabled={
                                !selectedItems[selectedTabs['right']] ||
                                selectedItems[selectedTabs['right']].size === 0
                            }
                            sx={{ marginBottom: '2rem' }}
                            startIcon={
                                <Icon
                                    name="ArrowSmall"
                                    color="#8b909a"
                                    fill="#8b909a"
                                    direction="left"
                                />
                            }
                            data-ai-explain="Remove selected applications from category"
                        >
                            {t('Remove')}
                        </RemoveButton>
                        <RemoveButton
                            onClick={() => {
                                handleRemoveItems(true)
                            }}
                            disabled={
                                !tabsRight.find(
                                    (t) => t.label === selectedTabs['right'],
                                )?.items?.length ||
                                tabsRight
                                    .find(
                                        (t) =>
                                            t.label === selectedTabs['right'],
                                    )
                                    ?.items?.every((item) =>
                                        disabledItems[
                                            selectedTabs['right']
                                        ]?.has(item.id),
                                    )
                            }
                            active={true}
                            sx={{ textAlign: 'center', alignContent: 'center' }}
                            startIcon={
                                <Icon
                                    name="ArrowSmall"
                                    color="#8b909a"
                                    fill="#8b909a"
                                    direction="left"
                                />
                            }
                            data-ai-explain="Remove all applications from category"
                        >
                            {t('RemoveAll')}
                        </RemoveButton>
                    </Box>

                    <Box>
                        <TabedLists
                            tabs={tabsRight}
                            selectedTabValue={selectedTabs['right']}
                            onSelectedTabChange={(val) => {
                                setSelectedTabs({ ...selectedTabs, right: val })
                            }}
                            handleSelectItem={(item) =>
                                handleSelectItem(item, selectedTabs['right'])
                            }
                            checkIfSelected={(item) =>
                                [
                                    ...(selectedItems[selectedTabs['right']] ||
                                        []),
                                ].some(
                                    (selectedItem) =>
                                        selectedItem.id === item.id,
                                )
                            }
                            checkIfDisabled={(item) =>
                                disabledItems[selectedTabs['right']]?.has(
                                    item.id,
                                )
                            }
                            handleDoubleClick={(e, item) => {
                                handleRemoveItems(false, item)
                            }}
                            searchTerm={searchTerm}
                            isLoading={isFetchingRight}
                        />
                        {tabsRight.find(
                            (t) => t.label === selectedTabs['right'],
                        )?.total > 0 && (
                            <PaginationMain margin="5px">
                                {!isFetchingRight && (
                                    <Pagination
                                        count={Math.ceil(
                                            (tabsRight.find(
                                                (t) =>
                                                    t.label ===
                                                    selectedTabs['right'],
                                            )?.total || 0) / ITEMS_PER_PAGE,
                                        )}
                                        size="large"
                                        page={selectedPages['right']}
                                        onChange={(_, value) =>
                                            setSelectedPages((prev) => ({
                                                ...prev,
                                                right: value,
                                            }))
                                        }
                                        variant="rounded"
                                    />
                                )}
                            </PaginationMain>
                        )}
                    </Box>
                </Box>
            </Box>
        )
    },
)

export default ApplicationsSelection
